import React from 'react';
import { Card } from '../ui/card';
import { PIPELINE_STAGES, getStageProgress, getStageColor } from './uploadStages';

// Per-file upload + parse progress tracker
export function UploadTracker({ files }) {
  if (!files || files.length === 0) return null;

  return (
    <Card
      data-testid="upload-tracker"
      style={{
        background: '#F1EAE1',
        border: '1px solid #CBD5E1',
        borderRadius: 14,
        padding: '1rem 1.25rem',
        marginBottom: '1rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
        <h4 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.8125rem', fontWeight: 600, color: '#0076A3' }}>
          Upload Progress
        </h4>
        <span style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>
          {files.filter(f => f.status === 'completed').length}/{files.length} completed
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {files.map((f, i) => {
          const pct = f.status === 'completed' ? 100 : getStageProgress(f.status, f.uploadPct || 0);
          const stage = PIPELINE_STAGES[f.status] || PIPELINE_STAGES.uploaded;
          const color = getStageColor(f.status);
          const isDone = f.status === 'completed';
          const isError = f.status === 'error';

          return (
            <div
              key={`upload-${f.name}-${i}`}
              data-testid="upload-tracker-file"
              style={{
                padding: '0.75rem',
                borderRadius: 10,
                background: isDone ? 'rgba(16,185,129,0.06)' : isError ? 'rgba(239,68,68,0.06)' : 'rgba(30,104,255,0.04)',
                border: `1px solid ${isDone ? 'rgba(16,185,129,0.2)' : isError ? 'rgba(239,68,68,0.2)' : 'rgba(30,104,255,0.12)'}`,
                transition: 'background 0.3s, border-color 0.3s',
              }}
            >
              {/* Row 1: file name + stage label */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <span style={{
                    fontSize: '0.8125rem', fontWeight: 600, color: '#002A38',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>
                    {f.name}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexShrink: 0 }}>
                  {!isDone && !isError && (
                    <span className="status-dot status-processing" style={{ width: 6, height: 6 }} />
                  )}
                  <span style={{
                    fontSize: '0.6875rem', fontWeight: 600, color,
                    fontFamily: 'JetBrains Mono',
                  }}>
                    {isError ? 'Error' : isDone ? 'Complete' : stage.label}
                  </span>
                </div>
              </div>

              {/* Row 2: progress bar + percentage */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                  <div
                    data-testid="upload-progress-bar"
                    style={{
                      height: '100%', borderRadius: 3,
                      background: isError
                        ? '#EF4444'
                        : isDone
                          ? '#10B981'
                          : `linear-gradient(90deg, #0076A3, ${color})`,
                      width: `${isError ? 100 : pct}%`,
                      transition: 'width 0.6s ease-out',
                    }}
                  />
                </div>
                <span style={{
                  fontSize: '0.6875rem', fontWeight: 600, color,
                  minWidth: 32, textAlign: 'right',
                  fontFamily: 'JetBrains Mono',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {isError ? '—' : `${pct}%`}
                </span>
              </div>

              {/* Row 3 (error only): error message */}
              {isError && f.errorMsg && (
                <div style={{ fontSize: '0.6875rem', color: '#DC2626', marginTop: '0.375rem' }}>
                  {f.errorMsg}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
